import { Card, Box, Typography, MenuItem, Select, SelectChangeEvent, Tooltip } from "@mui/material";
import { CRUD_GET_LIST, useGetMainList } from "ra-core";
import { useMemo } from "react";
import { ChangeEvent, FC, useCallback, useEffect, useState } from "react";
import { RecPatch } from "~/dataProvider/tools";
import { DatabaseResponseDto as BackupDbDto } from "~/gen/api/backup";
import { ObfuscationMappingResponseDto } from "~/gen/api/encrypt";
import { EncryptionMappingResponseDto as EncryptDbDto } from "~/gen/api/governance";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { useEncryptedDatabasesContext } from "./useEncryptedDatabases";
import OnDataLock from "~/icons/OnDataLock";


interface DatabaseData {
  name: string;
  databaseId: number;
}

export interface DatabaseSelectCardProps {
  preSelectDbId?: number;
  onChange: (e: ChangeEvent, selectedDb: BackupDbDto | undefined) => void;
  showDesc?: boolean;
}


type DatabaseDataSource = () => DatabaseData[];

export const useAllDatabasesSource: DatabaseDataSource = () => {
  const { data: databasesMap } = useGetMainList<BackupDbDto & RecPatch>('databases', {page:1, perPage: 1}, {field:'',order:''}, { action: CRUD_GET_LIST })
  const databases = useMemo(() => Object.values(databasesMap || {}).sort((a, b) => (a.name || '') < (b.name || '') ? -1 : 1), [databasesMap])
  return databases as DatabaseData[]
}

export const useEncryptableDatabasesSource: DatabaseDataSource = (skipFilter?: boolean) => {
  
  const { data: databasesMap } = useGetMainList<EncryptDbDto & RecPatch & {name:string}>('encryption/mappings', pages(), sorts(), {})
  const databases = Object.values(databasesMap || {}).sort((a, b) => (a.databaseName || '') < (b.databaseName || '') ? -1 : 1)
  .map(x => { x.name = x.databaseName || ''; return x; })
  
  return useMemo(() => databases.filter(x => skipFilter || (x.mappedCount ?? 0) > 0 || (x.skippedCount ?? 0) > 0), [databases, skipFilter]) as DatabaseData[]
}

export const useObfuscatableDatabasesSource: DatabaseDataSource = () => {
  const { data: databasesMap } = useGetMainList<EncryptDbDto & RecPatch & {name:string}>('encryption/obfs/mappings', pages(), sorts(), {})
  return useMemo(() => 
    Object.values(databasesMap || {})
      .filter(x => ((x as ObfuscationMappingResponseDto)?.obfsMappedCount || 0) > 0)
      .sort((a, b) => (a.databaseName || '') < (b.databaseName || '') ? -1 : 1)
      .map(x => { x.name = x.databaseName || ''; return x; }) as DatabaseData[],
    [databasesMap])
}

export const useDatabaseSelectCard = (dataSource: DatabaseDataSource): FC<DatabaseSelectCardProps> => {
  const databases = dataSource()

  return useCallback((props: DatabaseSelectCardProps) => (
    <DatabaseSelectCard databases={databases} {...props} />
  ), [databases])
}

const DatabaseSelectCard: FC<DatabaseSelectCardProps & { databases: DatabaseData[] }> = ({ 
  onChange,
  preSelectDbId,
  databases,
  showDesc,
}) => {
  const { encryptedDbMap } = useEncryptedDatabasesContext()
  const showTip = Boolean(preSelectDbId && encryptedDbMap && encryptedDbMap[preSelectDbId])

  const [currDb, setCurrDb] = useState(databases.find(d => d.databaseId === preSelectDbId))

  const handleChange = useCallback((e: SelectChangeEvent<DatabaseData | undefined>) => {
    if (!e.target.value) return

    const chosenDb = e.target.value as DatabaseData
    setCurrDb(chosenDb)
    onChange(e as any, chosenDb)
  }, [databases])

  useEffect(() => {
    if (preSelectDbId) {
      setCurrDb(databases.find(d => d.databaseId === preSelectDbId))
    }
  }, [preSelectDbId, databases])
  
  return (
    <Card>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 1,
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
          <Typography
            color="textPrimary"
            variant="h6"
            sx={{ mr: 'auto' }}
          >
            Database
          </Typography>
          {showTip &&
            <Tooltip title="Encrypted Database">
              <OnDataLock transform="scale(0.7)" color="primary" />
            </Tooltip>}
        </Box>
        <Box
          sx={{ flex: 1 }}
        >
          <Select
            fullWidth
            name="option"
            onChange={handleChange}
            size="small"
            value={currDb || '' as any}
            variant="outlined"
          >
            {databases.map(d => (
              <MenuItem key={d.databaseId} value={d as any}>{d.name}</MenuItem>
              ))}
          </Select>
        </Box>
        {showDesc &&
          <Box
            sx={{ flex: 2, ml: 4 }}
          >
            <Typography variant="body2" sx={{ my: 1 }}>{(currDb as any)?.description}</Typography>
          </Box>}
      </Box>
    </Card>
  )
}

